import {
  calcBaseAnual,
  calcImpactoLiquido,
  calcRegimeAtual,
  calcRegimeFuturo,
} from "../utils/calculoReforma";

/**
 * Dados da aba Simulação, sempre pelo motor de cálculo das outras abas. A API devolve
 * o total do período pedido, então a tela fala em "no período", e não em "por ano".
 */

const pct = (part, whole) => (whole ? (part / whole) * 100 : 0);

function compute(fiscalData, params) {
  const despesas = fiscalData.creditos?.totalCreditoConfirmado?.valorAnual || 0;
  const base = calcBaseAnual(fiscalData.totalMensal, despesas, params);
  const atual = calcRegimeAtual(fiscalData.totalMensal, base, params);
  const futuro = calcRegimeFuturo(base, params);
  const impacto = calcImpactoLiquido(atual, futuro, base);
  return { base, atual, futuro, impacto };
}

// Efeito de 1 ponto a mais em cada premissa sobre a diferença entre os regimes
function buildSensitivity(fiscalData, params, meta) {
  const reference = compute(fiscalData, params).impacto.variacaoAbsoluta;
  return meta
    .map((m) => {
      const next = { ...params, [m.id]: Number(params[m.id] || 0) + 1 };
      return { key: m.id, label: m.label, current: Number(params[m.id] || 0), delta: compute(fiscalData, next).impacto.variacaoAbsoluta - reference };
    })
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
}

const taxRow = (key, label, debit, credit, net, note) => ({
  key,
  label,
  debit,
  credit,
  net,
  note,
  coverage: pct(credit, debit),
  segments: [{ key: "credit", share: pct(credit, debit), kind: "credit" }],
});

const futureRow = (key, label, regime) => {
  const credit = regime.creditoCogs + regime.creditoDespesas;
  return {
    key,
    label,
    debit: regime.debito,
    credit,
    net: regime.liquido,
    creditCogs: regime.creditoCogs,
    creditExpenses: regime.creditoDespesas,
    coverage: pct(credit, regime.debito),
    segments: [
      { key: "cogs", share: pct(regime.creditoCogs, regime.debito), kind: "credit" },
      { key: "expenses", share: pct(regime.creditoDespesas, regime.debito), kind: "extra" },
    ],
  };
};

export function buildSimulation(fiscalData, params, defaults, meta) {
  const { base, atual, futuro, impacto } = compute(fiscalData, params);
  const reference = compute(fiscalData, defaults).impacto.variacaoAbsoluta;
  const grossMargin = base.faturamento - base.cogsTotal;

  return {
    impact: {
      value: impacto.variacaoAbsoluta,
      percent: impacto.variacaoPercentual * 100,
      extraCredit: impacto.creditoAdicional,
      reference,
      fromReference: impacto.variacaoAbsoluta - reference,
    },
    margin: { value: base.margemTributavel, share: pct(base.margemTributavel, base.faturamento) },
    flow: {
      total: base.faturamento,
      revenueVehicles: base.receitaVeiculos,
      revenueParts: base.receitaPecasServ,
      costVehicles: base.cogsVeiculos,
      costParts: base.cogsPecasServ,
      costTotal: base.cogsTotal,
      grossMargin,
      expenses: base.despesasElegiveis,
      taxable: base.margemTributavel,
    },
    current: {
      total: atual.total,
      rate: pct(atual.total, base.faturamento),
      rows: [
        taxRow("pis", "PIS", atual.pis.debito, atual.pis.credito, atual.pis.liquido),
        taxRow("cofins", "COFINS", atual.cofins.debito, atual.cofins.credito, atual.cofins.liquido),
        taxRow("icms", "ICMS", atual.icms.debito, atual.icms.credito, atual.icms.liquido),
        taxRow("iss", `ISS (${params.issAtualServicos}%)`, atual.iss.debito, 0, atual.iss.liquido, "Estimado, sem crédito"),
      ],
    },
    future: {
      total: futuro.total,
      rate: pct(futuro.total, base.faturamento),
      rows: [futureRow("cbs", "CBS", futuro.cbs), futureRow("ibs", "IBS", futuro.ibs)],
    },
    sensitivity: buildSensitivity(fiscalData, params, meta),
  };
}
